import { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CloudUpload, Download, ShieldCheck } from "lucide-react";

const human = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

/** Your data, in your hands — independent of the server it runs on. */
export function BackupCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/backup/status")
      .then(({ data }) => setStatus(data))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const grab = async (path, filename) => {
    setBusy(true);
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded — keep it somewhere safe");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const offsite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/offsite");
      toast.success(`Copy stored off the server · ${(data.size / 1024).toFixed(0)} KB`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleAuto = async (next) => {
    setBusy(true);
    try {
      await api.post("/admin/backup/auto", { auto: next });
      toast.success(next ? "Hourly off-server backups are ON" : "Hourly backups paused");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const stamp = new Date().toISOString().slice(0, 10);

  return (
    <Card className="p-5 bg-[#0a0c0b] ring-1 ring-white/[0.07] border-0" data-testid="backup-card">
      <div className="flex items-center gap-2.5">
        <ShieldCheck className="w-[18px] h-[18px] text-emerald-400" strokeWidth={2} />
        <div className="text-[10px] uppercase tracking-widest text-emerald-400">Data backup</div>
      </div>
      <p className="mt-2 text-[12.5px] text-white/45 leading-relaxed">
        Everything that would hurt to lose — registrations, payment claims, WhatsApp numbers,
        answers and attendance. Take a copy any time; if the server ever dies, your records don't
        go with it.
      </p>

      {status && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-white/55">
          <span data-testid="backup-counts">
            {status.counts?.payments ?? 0} payments · {status.counts?.wa_submissions ?? 0} leads ·{" "}
            {status.counts?.attendance ?? 0} attendance rows
          </span>
          <span className="text-white/30">
            Last copy off the server: {human(status.last_offsite_at)}
          </span>
        </div>
      )}

      <button
        onClick={() => toggleAuto(!status?.auto)}
        disabled={busy || !status}
        className={`mt-3.5 w-full flex items-center gap-3 px-4 py-3 rounded-2xl ring-1 transition-colors text-left ${
          status?.auto
            ? "bg-emerald-500/[0.12] ring-emerald-400/30"
            : "bg-white/[0.03] ring-white/[0.08]"
        }`}
        data-testid="backup-auto-toggle"
      >
        <span
          className={`w-10 h-6 shrink-0 rounded-full p-0.5 transition-colors ${
            status?.auto ? "bg-emerald-400" : "bg-white/15"
          }`}
        >
          <span
            className={`block w-5 h-5 rounded-full bg-white transition-transform ${
              status?.auto ? "translate-x-4" : ""
            }`}
          />
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-white">
            Hourly backup, on its own
          </span>
          <span className="block text-[11.5px] text-white/40">
            {status?.auto
              ? "A copy goes to cloud storage every hour — nothing for you to click"
              : "Paused — you'll have to take copies yourself"}
          </span>
        </span>
      </button>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={() => grab("/admin/backup", `udyog-backup-${stamp}.json`)}
          disabled={busy}
          className="rounded-full h-10 px-4 bg-yellow-300 hover:bg-yellow-200 text-black text-[11px] font-bold uppercase tracking-[0.12em]"
          data-testid="backup-download-json"
        >
          <Download className="w-3.5 h-3.5 mr-2" /> Download everything
        </Button>
        <Button
          onClick={() =>
            grab("/admin/backup.csv?collection=payments", `udyog-payments-${stamp}.csv`)
          }
          disabled={busy}
          variant="ghost"
          className="rounded-full h-10 px-4 ring-1 ring-white/[0.12] text-[11px] uppercase tracking-[0.12em] text-white/60 hover:text-white"
          data-testid="backup-download-payments"
        >
          Payments CSV
        </Button>
        <Button
          onClick={() =>
            grab("/admin/backup.csv?collection=wa_submissions", `udyog-leads-${stamp}.csv`)
          }
          disabled={busy}
          variant="ghost"
          className="rounded-full h-10 px-4 ring-1 ring-white/[0.12] text-[11px] uppercase tracking-[0.12em] text-white/60 hover:text-white"
          data-testid="backup-download-leads"
        >
          Leads CSV
        </Button>
        <Button
          onClick={offsite}
          disabled={busy}
          variant="ghost"
          className="rounded-full h-10 px-4 ring-1 ring-emerald-400/30 text-[11px] uppercase tracking-[0.12em] text-emerald-300 hover:text-white"
          data-testid="backup-offsite"
        >
          <CloudUpload className="w-3.5 h-3.5 mr-2" /> Store a copy off the server
        </Button>
      </div>

      <p className="mt-3 text-[11.5px] text-white/25">
        Tip: a cron job can call <code className="text-white/40">POST {API}/admin/backup/offsite</code>{" "}
        hourly during class hours, so a copy always lives in cloud storage, not on the VPS.
      </p>
    </Card>
  );
}
